import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { SIC_CODES, formatSicCode } from "@shared/sicCodes";

interface EditEntityFieldsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entity: {
    id: number;
    name: string;
    sicCodeId?: number | null;
    sic?: string | null;
    zipCode?: string | null;
  } | null;
  entityType: "client" | "prospect";
  missingFields: string[];
  onSuccess?: (updatedEntity: any) => void;
}

export function EditEntityFieldsDialog({
  open,
  onOpenChange,
  entity,
  entityType,
  missingFields,
  onSuccess,
}: EditEntityFieldsDialogProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [sicOpen, setSicOpen] = useState(false);
  const [sicCodeId, setSicCodeId] = useState<number | undefined>(() => {
    const initial = entity?.sicCodeId;
    const parsed = typeof initial === "string" ? Number(initial) : initial;
    return Number.isFinite(parsed as number) ? (parsed as number) : undefined;
  });
  const [zipCode, setZipCode] = useState(entity?.zipCode ?? "");

  // Reset values when dialog opens with new entity
  useEffect(() => {
    if (entity) {
      const nextSic =
        typeof entity.sicCodeId === "string"
          ? Number(entity.sicCodeId)
          : entity.sicCodeId;
      setSicCodeId(Number.isFinite(nextSic as number) ? (nextSic as number) : undefined);
      setZipCode(entity.zipCode ?? "");
    }
  }, [entity]);

  const sicOptions = useMemo(
    () =>
      SIC_CODES.map((sic) => ({
        id: sic.code,
        code: sic.code,
        industryTitle: sic.industryTitle,
      })),
    []
  );

  const selectedSic = sicOptions.find((opt) => opt.id === sicCodeId);

  const updateMutation = useMutation({
    mutationFn: async (data: { sicCodeId?: number; zipCode?: string }) => {
      if (!entity) throw new Error("No entity selected");
      const endpoint =
        entityType === "client"
          ? `/api/clients/${entity.id}`
          : `/api/prospects/${entity.id}`;
      const response = await apiRequest("PATCH", endpoint, data);
      if (!response.ok) {
        throw new Error(`Failed to update ${entityType}`);
      }
      return response.json();
    },
    onSuccess: (updatedEntity) => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      queryClient.invalidateQueries({ queryKey: ["/api/prospects"] });
      if (entity) {
        queryClient.invalidateQueries({
          queryKey: [`/api/${entityType}s`, entity.id.toString()],
        });
      }
      toast({
        title: "Updated successfully",
        description: `${entityType === "client" ? "Client" : "Prospect"} information has been updated.`,
        variant: "success",
      });
      onSuccess?.(updatedEntity);
      onOpenChange(false);
    },
    onError: (error) => {
      console.error("Update error:", error);
      toast({
        title: "Update failed",
        description: `Failed to update ${entityType}. Please try again.`,
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    const updates: { sicCodeId?: number; zipCode?: string } = {};

    if (missingFields.includes("SIC Code") && sicCodeId != null) {
      const parsedSic =
        typeof sicCodeId === "string" ? Number(sicCodeId) : sicCodeId;
      if (Number.isFinite(parsedSic)) {
        updates.sicCodeId = parsedSic as number;
      }
    }

    if (missingFields.includes("ZIP Code") && zipCode.trim()) {
      updates.zipCode = zipCode.trim();
    }

    if (Object.keys(updates).length === 0) {
      toast({
        title: "No changes",
        description: "Please fill in the required fields.",
        variant: "destructive",
      });
      return;
    }

    updateMutation.mutate(updates);
  };

  const showSicField = missingFields.includes("SIC Code");
  const showZipField = missingFields.includes("ZIP Code");

  if (!entity) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Update Required Information</DialogTitle>
          <DialogDescription>
            The following information is required for <strong>{entity.name}</strong> before proceeding with the {entityType === "client" ? "quote" : "RFP"}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {showZipField && (
            <div className="space-y-2">
              <Label htmlFor="zipCode">ZIP Code</Label>
              <Input
                id="zipCode"
                placeholder="Enter ZIP code (e.g., 90210)"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                maxLength={10}
              />
            </div>
          )}

          {showSicField && (
            <div className="space-y-2">
              <Label>SIC Code</Label>
              <Popover open={sicOpen} onOpenChange={setSicOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={sicOpen}
                    className="w-full justify-between"
                  >
                    <span className="truncate">
                      {selectedSic
                        ? `${formatSicCode(selectedSic.code)} - ${selectedSic.industryTitle}`
                        : "Select SIC code..."}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[--radix-popover-trigger-width] p-0"
                  onWheel={(e) => e.stopPropagation()}
                >
                  <Command>
                    <CommandInput placeholder="Search by code or industry..." />
                    <CommandEmpty>No SIC code found.</CommandEmpty>
                    <CommandList style={{ maxHeight: '250px', overflowY: 'auto' }}>
                      <CommandGroup>
                        {sicOptions.map((opt) => (
                          <CommandItem
                            key={opt.id}
                            value={`${formatSicCode(opt.code)} ${opt.industryTitle}`}
                            onSelect={() => {
                              setSicCodeId(opt.id);
                              setSicOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                sicCodeId === opt.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <span className="truncate">
                              {formatSicCode(opt.code)} - {opt.industryTitle}
                            </span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={updateMutation.isPending}>
            {updateMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
